'use client';

import { useEffect, useState, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useCart } from '../contexts/CartContext';
import SponsoredBanners from './SponsoredBanners';
import ChevronDownIcon from '../icons/ChevronDownIcon';
import ChevronUpIcon from '../icons/ChevronUpIcon';
import ArrowNarrowRightIcon from '../icons/ArrowNarrowRightIcon';
import { Button } from '@/components';

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:4000';

// Brand Logo Image Component with fallback
function BrandLogoImage({ logoUri, brandName, isActive }: { logoUri: string; brandName: string; isActive: boolean }) {
  const [imageError, setImageError] = useState(false);

  if (imageError) {
    return (
      <div className="text-center w-full h-full flex items-center justify-center">
        <div className="text-xl font-bold text-text-primary">{brandName}</div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      <Image
        src={logoUri}
        alt={`${brandName} brand logo`}
        fill
        className="object-contain"
        onError={() => setImageError(true)}
        loading="lazy"
        unoptimized
        sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
      />
    </div>
  );
}

type Brand = {
  id: string;
  name: string;
  logoUri: string | null;
  isDisplayed?: boolean;
  displayOrder?: number | null;
};

export default function BrandsList() {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const brandsContainerRef = useRef<HTMLDivElement>(null);
  const { state } = useCart();

  useEffect(() => {
    loadBrands();
  }, [state.storeCovaLocationId]);

  const loadBrands = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (state.storeCovaLocationId) {
        params.set('covaLocationId', String(state.storeCovaLocationId));
      }

      const url = `${API_BASE}/api/brands/displayed?limit=12${params.toString() ? `&${params.toString()}` : ''}`;
      const res = await fetch(url, { cache: 'no-store' });
      
      if (!res.ok) {
        throw new Error(`Failed to load brands: ${res.status}`);
      }
      
      const data = await res.json();
      if (data?.success) {
        // Get all active brands (no limit, or use a higher limit)
        const urlAll = `${API_BASE}/api/brands/displayed?limit=100${params.toString() ? `&${params.toString()}` : ''}`;
        const resAll = await fetch(urlAll, { cache: 'no-store' });
        if (resAll.ok) {
          const dataAll = await resAll.json();
          if (dataAll?.success) {
            const brandsList = dataAll.brands || [];
            setBrands(brandsList);
          }
        } else {
          // Fallback to limited brands if all brands fetch fails
          const brandsList = data.brands || [];
          setBrands(brandsList);
        }
      }
    } catch (err: unknown) {
      // Silently handle errors - backend may not be available during SSR
      if (process.env.NODE_ENV === 'development') {
        const errorMessage = err instanceof Error ? err.message : String(err);
        console.warn('Failed to load brands (backend may not be running):', errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Toggle expand/collapse. On collapse, bring the grid's top back into view so
  // the user isn't stranded far down the page after the list shrinks.
  const handleToggleShowAll = () => {
    const next = !showAll;
    setShowAll(next);
    if (!next) {
      requestAnimationFrame(() => {
        brandsContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => (
          <div key={i} className="flex flex-col bg-constant-white rounded-2xl border border-neutral-10 shadow-md p-4 animate-pulse">
            <div className="flex-1 rounded-xl border-2 border-neutral-10 bg-blue-5 mb-4 aspect-[3/2]" />
            <div className="h-10 rounded-lg bg-neutral-10" />
          </div>
        ))}
      </div>
    );
  }

  if (brands.length === 0) {
    return null;
  }

  // Filter to only active brands
  const activeBrands = brands.filter(brand => brand.isDisplayed !== false);

  // Collapsed = first COLLAPSED_COUNT brands (2 full rows on desktop). Rendering
  // a slice — not a max-height cap — guarantees the list is actually collapsed
  // on load regardless of card heights, so it matches the "View more" button.
  const COLLAPSED_COUNT = 8;
  const hasMoreBrands = activeBrands.length > COLLAPSED_COUNT;
  const brandsToShow = showAll || !hasMoreBrands ? activeBrands : activeBrands.slice(0, COLLAPSED_COUNT);

  return (
    <div className="space-y-6">
      {/* Brands Grid */}
      <div className="relative">
        <div
          ref={brandsContainerRef}
          id="brands-grid"
          className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 gap-4"
          role="list"
          aria-label="Popular cannabis brands"
        >
        {brandsToShow.map((brand) => {
          const isActive = brand.isDisplayed !== false;
          const brandSearchUrl = `/search?brand=${encodeURIComponent(brand.name)}`;
          
          return (
            <div
              key={brand.id}
              role="listitem"
              className={`group/card relative flex flex-col bg-constant-white rounded-2xl border border-neutral-10 shadow-none transition-all overflow-hidden hover:border-blue-80 hover:shadow-md ${
                !isActive ? 'opacity-50' : ''
              }`}
            >
              {/* Brand Logo Section - Inner Container */}
              <div className={`flex-1 flex items-center justify-center p-4 ${isActive ? '' : 'grayscale'}`}>
                <div className={`relative w-full aspect-[1/0.56] rounded-xl border-2 border-neutral-10 bg-blue-5 flex items-center justify-center p-4 ${!isActive ? 'opacity-50' : ''}`}>
                  {brand.logoUri ? (
                    <BrandLogoImage 
                      logoUri={brand.logoUri} 
                      brandName={brand.name}
                      isActive={isActive}
                    />
                  ) : (
                    <div className="text-center w-full">
                      <div className="text-xl font-bold text-text-primary">{brand.name}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Shop Brand Button — fills navy + nudges its arrow on card hover */}
              <div className="px-4 pb-4">
                <Button
                  asChild
                  variant="bordered"
                  color="white"
                  size="lg"
                  endIcon={
                    <ArrowNarrowRightIcon
                      size={16}
                      color="currentColor"
                      aria-hidden="true"
                      className="transition-transform duration-200 group-hover/card:translate-x-1"
                    />
                  }
                  className={`w-full rounded-xl py-3 transition-colors duration-200 group-hover/card:border-blue-100 group-hover/card:bg-blue-100 group-hover/card:text-constant-white ${
                    isActive ? '' : 'pointer-events-none'
                  }`}
                >
                  <Link href={brandSearchUrl} aria-label={`Shop ${brand.name} brand products`}>
                    Shop brand
                  </Link>
                </Button>
              </div>
            </div>
          );
        })}
        </div>

        {/* Bottom fade — hints there are more brands behind the "View more" button. */}
        {!showAll && hasMoreBrands && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-28 rounded-b-2xl bg-gradient-to-t from-[#F7F7F8] via-[#F7F7F8]/85 to-transparent"
          />
        )}
      </div>

      {/* View More / Show Less toggle */}
      {hasMoreBrands && (
        <div className="w-full">
          <Button
            type="button"
            onClick={handleToggleShowAll}
            variant="primary"
            size="lg"
            aria-expanded={showAll}
            aria-controls="brands-grid"
            endIcon={
              showAll ? (
                <ChevronUpIcon
                  size={16}
                  color="currentColor"
                  aria-hidden="true"
                  className="transition-transform duration-200 group-hover:-translate-y-0.5"
                />
              ) : (
                <ChevronDownIcon
                  size={16}
                  color="currentColor"
                  aria-hidden="true"
                  className="transition-transform duration-200 group-hover:translate-y-0.5"
                />
              )
            }
            className="h-12 w-full rounded-xl text-body-16-desktop font-semibold"
          >
            {showAll ? 'Show fewer brands' : 'View more brands'}
          </Button>
        </div>
      )}
    </div>
  );
}
